import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchWalletConnection,
  fetchWalletMappings,
  fetchPendingTransactions,
  fetchAllTransactions,
  fetchPendingTransactionCount,
  connectWallet,
  disconnectWallet,
  testWalletConnection,
  listWalletAccounts,
  triggerWalletSync,
  approveTransactions,
  dismissTransactions,
  createWalletMapping,
  updateWalletMapping,
  deleteWalletMapping,
} from '@/api/wallet';

// ── Queries ──

export function useWalletConnection() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet', 'connection', user?.id],
    queryFn: () => fetchWalletConnection(user!.id),
    enabled: !!user,
  });
}

export function useWalletMappings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet', 'mappings', user?.id],
    queryFn: () => fetchWalletMappings(user!.id),
    enabled: !!user,
  });
}

export function usePendingTransactions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet', 'transactions', 'pending', user?.id],
    queryFn: () => fetchPendingTransactions(user!.id),
    enabled: !!user,
  });
}

export function useWalletTransactions(status?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet', 'transactions', status ?? 'all', user?.id],
    queryFn: () => fetchAllTransactions(user!.id, status),
    enabled: !!user,
  });
}

/**
 * Live account list from the Wallet API. Accounts are created in the Wallet
 * app, so the list is refetched when the user comes back to this tab and can
 * be refreshed on demand.
 */
export function useWalletAccounts(enabled: boolean) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet', 'accounts', user?.id],
    queryFn: () => listWalletAccounts(),
    enabled: enabled && !!user,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });
}

export function usePendingTransactionCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet', 'pending-count', user?.id],
    queryFn: () => fetchPendingTransactionCount(user!.id),
    enabled: !!user,
    // Refresh every 5 minutes for sidebar badge
    refetchInterval: 5 * 60 * 1000,
  });
}

// ── Mutations ──

export function useConnectWallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (walletApiToken: string) => connectWallet(walletApiToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
  });
}

export function useDisconnectWallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => disconnectWallet(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
  });
}

export function useTestWalletConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => testWalletConnection(),
    // A test pauses or re-activates the connection server-side (token, Premium,
    // API consent), so the connection card must re-read it.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
  });
}

export function useTriggerWalletSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => triggerWalletSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', 'pending-count'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', 'connection'] });
    },
  });
}

export function useApproveTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionIds,
      overrides,
    }: {
      transactionIds: string[];
      overrides?: { description?: string; category_id?: string };
    }) => approveTransactions(transactionIds, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', 'pending-count'] });
      // Also invalidate expenses since new ones were created
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
    },
  });
}

export function useDismissTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionIds: string[]) => dismissTransactions(transactionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', 'pending-count'] });
    },
  });
}

export function useCreateWalletMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWalletMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'mappings'] });
    },
  });
}

export function useUpdateWalletMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappingId, updates }: {
      mappingId: string;
      updates: { group_id?: string; is_active?: boolean; auto_approve?: boolean };
    }) => updateWalletMapping(mappingId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'mappings'] });
    },
  });
}

export function useDeleteWalletMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) => deleteWalletMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'mappings'] });
    },
  });
}
